import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/app/ProtectedRoute";
import CommandPalette from "@/components/app/CommandPalette";
import TrialLimitModal from "@/components/app/TrialLimitModal";

// Optional Sentry — activates only when REACT_APP_SENTRY_DSN is provided.
// PII is scrubbed via beforeSend so user emails/names never leave our infra.
if (process.env.REACT_APP_SENTRY_DSN) {
  try {
    /* eslint-disable */
    const Sentry = require("@sentry/react");
    /* eslint-enable */
    Sentry.init({
      dsn: process.env.REACT_APP_SENTRY_DSN,
      tracesSampleRate: 0.05,
      sendDefaultPii: false,
      beforeSend(event) {
        if (event.user) { delete event.user.email; delete event.user.username; }
        return event;
      },
    });
  } catch { /* sentry not installed yet — that's fine */ }
}

// PostHog — activates only when REACT_APP_POSTHOG_KEY is set AND the cookie
// consent has accepted analytics. We import on-demand to keep the initial bundle small.
async function initPostHogIfConsented() {
  if (!process.env.REACT_APP_POSTHOG_KEY) return;
  try {
    const consent = JSON.parse(localStorage.getItem("compliche.cookieConsent") || "{}");
    if (consent.analytics === false) return;
    const { default: posthog } = await import("posthog-js");
    posthog.init(process.env.REACT_APP_POSTHOG_KEY, {
      api_host: process.env.REACT_APP_POSTHOG_HOST || "https://eu.posthog.com",
      persistence: "localStorage",
      disable_session_recording: true,
      property_blacklist: ["$ip"],
    });
    window.__posthog = posthog;
  } catch { /* fine if not installed */ }
}
if (typeof window !== "undefined") initPostHogIfConsented();

/**
 * Cross-section scroll behaviour. React Router does not scroll on route change
 * by default — this brings parity with native browser behaviour and adds
 * hash-anchor support so cross-section links like /security#disclosure or
 * /app/settings#security land on the right tab / section.
 */
function ScrollToHash() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const id = hash.slice(1);
      // Defer to next frame so the target page has had a chance to render.
      requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    } else {
      window.scrollTo(0, 0);
    }
    // PostHog $pageview — only fires if the SDK is initialised.
    try {
      const ph = window.__compliche_posthog;
      if (ph) ph.capture("$pageview", { path: pathname });
    } catch { /* ignore */ }
  }, [pathname, hash]);
  return null;
}

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import SignUp from "@/pages/SignUp";
import TwoFactor from "@/pages/TwoFactor";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import AcceptInvite from "@/pages/AcceptInvite";

import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import Flows from "@/pages/Flows";
import FlowBuilder from "@/pages/FlowBuilder";
import Clients from "@/pages/Clients";
import ClientDetail from "@/pages/ClientDetail";
import ComplianceChecker from "@/pages/ComplianceChecker";
import AuditTrail from "@/pages/AuditTrail";
import ClientPortal from "@/pages/ClientPortal";
import SecurityPage from "@/pages/SecurityPage";
import TemplatesLibrary from "@/pages/TemplatesLibrary";
import SettingsPage from "@/pages/SettingsPage";
import { Documents, Help } from "@/pages/Misc";
import DocumentLibrary from "@/pages/DocumentLibrary";
import Developers from "@/pages/Developers";
import SuperAdmin from "@/pages/SuperAdmin";
import MultiFirm from "@/pages/MultiFirm";
import { Complaints, VulnerableCustomers, AnnualReviews, Supervision, AmlScreenings } from "@/pages/ProFeatures";

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToHash />
        <CommandPalette />
        <TrialLimitModal />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/two-factor" element={<TwoFactor />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route path="/security" element={<SecurityPage />} />
          <Route path="/developers" element={<Developers />} />
          <Route path="/admin" element={<SuperAdmin />} />
          <Route path="/portal/:token" element={<ClientPortal />} />

          <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />

          <Route path="/dashboard" element={<Navigate to="/app/dashboard" replace />} />

          <Route path="/app/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/app/flows" element={<ProtectedRoute><Flows /></ProtectedRoute>} />
          <Route path="/app/flows/new" element={<ProtectedRoute><Flows /></ProtectedRoute>} />
          <Route path="/app/flows/:id/edit" element={<ProtectedRoute><FlowBuilder /></ProtectedRoute>} />
          <Route path="/app/clients" element={<ProtectedRoute><Clients /></ProtectedRoute>} />
          <Route path="/app/clients/:id" element={<ProtectedRoute><ClientDetail /></ProtectedRoute>} />
          <Route path="/app/compliance" element={<ProtectedRoute><ComplianceChecker /></ProtectedRoute>} />
          <Route path="/app/audit-trail" element={<ProtectedRoute roles={["firm_admin", "compliance_manager", "read_only"]}><AuditTrail /></ProtectedRoute>} />
          <Route path="/app/templates" element={<ProtectedRoute><TemplatesLibrary /></ProtectedRoute>} />
          <Route path="/app/documents" element={<ProtectedRoute><DocumentLibrary /></ProtectedRoute>} />
          <Route path="/app/complaints" element={<ProtectedRoute><Complaints /></ProtectedRoute>} />
          <Route path="/app/vulnerable-customers" element={<ProtectedRoute><VulnerableCustomers /></ProtectedRoute>} />
          <Route path="/app/annual-reviews" element={<ProtectedRoute><AnnualReviews /></ProtectedRoute>} />
          <Route path="/app/supervision" element={<ProtectedRoute><Supervision /></ProtectedRoute>} />
          <Route path="/app/aml" element={<ProtectedRoute><AmlScreenings /></ProtectedRoute>} />
          <Route path="/app/multi-firm" element={<ProtectedRoute><MultiFirm /></ProtectedRoute>} />
          <Route path="/app/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
          <Route path="/app/security" element={<Navigate to="/app/settings#security" replace />} />
          <Route path="/app/help" element={<ProtectedRoute><Help /></ProtectedRoute>} />
          <Route path="/app/profile" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors closeButton />
    </AuthProvider>
  );
}

export default App;
